import React, { useEffect, useState } from 'react'
import ContainerDashboard from '../../components/layouts/ContainerDashboard'
import Loader from '../Loader'
import swal from 'sweetalert'
import Pagination, { PerPageSelect } from '../../components/Pagination'
import { axiosInstance } from '../Config'
import restrictedImg from "../../Assets/Images/restricted.png"
import { usePermissionData } from '../../AppContext'

const labelStyle = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#999' }

const RestrictedWords = () => {
    const { checkAccess } = usePermissionData()
    const [words, setWords]               = useState([])
    const [filteredWords, setFilteredWords] = useState([])
    const [loading, setLoading]           = useState(false)
    const [searchQuery, setSearchQuery]   = useState('')

    const [page, setPage]                 = useState(1)
    const [itemsPerPage, setItemsPerPage] = useState(20)

    const [showModal, setShowModal]       = useState(false)
    const [modalMode, setModalMode]       = useState('add')
    const [editingIndex, setEditingIndex] = useState(null)
    const [inputWord, setInputWord]       = useState('')
    const [wordError, setWordError]       = useState('')
    const [submitting, setSubmitting]     = useState(false)

    useEffect(() => {
        document.title = 'Restricted Words | Mindora'
        window.scrollTo(0, 0)
        fetchWords()
    }, [])

    const fetchWords = async () => {
        setLoading(true)
        try {
            const res = await axiosInstance.get('restricted-words')
            const arr = (res.data.data.words || '').split(',').map(w => w.trim()).filter(Boolean)
            setWords(arr)
            setFilteredWords(arr)
        } catch (err) {
            swal('', err?.response?.data?.message || 'Failed to load restricted words.', 'error')
        } finally {
            setLoading(false)
        }
    }

    const saveWords = async (updatedWords) => {
        const res = await axiosInstance.post('restricted-words', { words: updatedWords.join(',') })
        return res
    }

    const applySearch = (list, query) => {
        if (!query.trim()) setFilteredWords(list)
        else setFilteredWords(list.filter(w => w.toLowerCase().includes(query.toLowerCase())))
    }

    const onSearch = (e) => {
        const query = e.target.value
        setSearchQuery(query)
        setPage(1)
        applySearch(words, query)
    }

    const openAdd = () => {
        setModalMode('add')
        setEditingIndex(null)
        setInputWord('')
        setWordError('')
        setShowModal(true)
    }

    const openEdit = (word) => {
        const idx = words.indexOf(word)
        setModalMode('edit')
        setEditingIndex(idx)
        setInputWord(word)
        setWordError('')
        setShowModal(true)
    }

    const closeModal = () => {
        setShowModal(false)
        setEditingIndex(null)
        setInputWord('')
        setWordError('')
    }

    const handleSubmit = async() => {
        const trimmed = inputWord.trim().toLowerCase()
        if (!trimmed) { setWordError('Please enter a word.'); return }

        const isDuplicate = words.some((w, i) => {
            if (modalMode === 'edit' && i === editingIndex) return false
            return w.toLowerCase() === trimmed
        })
        if (isDuplicate) { setWordError(`"${trimmed}" already exists in the restricted list.`); return }

        setSubmitting(true)
        const updatedWords = modalMode === 'edit'
            ? words.map((w, i) => i === editingIndex ? trimmed : w)
            : [trimmed, ...words]
        try {
            const res = await saveWords(updatedWords)
            setWords(updatedWords)
            applySearch(updatedWords, searchQuery)
            closeModal()
            swal('', res?.data?.message || (modalMode === 'edit' ? 'Word updated successfully!' : 'Word added successfully!'), 'success')
        } catch (err) {
            swal('', err?.response?.data?.message || 'Failed to save word. Please try again.', 'error')
        } finally {
            setSubmitting(false)
        }
    }

    const handleDelete = (word) => {
        swal({
            title: `Delete "${word}"?`,
            text: 'This word will be removed from the restricted list.',
            icon: 'warning',
            buttons: true,
            dangerMode: true,
        }).then(async (will) => {
            if (will) {
                const updatedWords = words.filter(w => w !== word)
                setLoading(true)
                try {
                    const res = await saveWords(updatedWords)
                    setWords(updatedWords)
                    applySearch(updatedWords, searchQuery)
                    swal('', res?.data?.message || `"${word}" removed successfully!`, 'success')
                } catch (err) {
                    swal('', err?.response?.data?.message || 'Failed to delete word. Please try again.', 'error')
                } finally {
                    setLoading(false)
                }
            }
        })
    }

    const totalPages = Math.ceil(filteredWords.length / itemsPerPage)
    const pagedWords = filteredWords.slice((page - 1) * itemsPerPage, page * itemsPerPage)

    const WordCard = ({ word, serial }) => (
        <div className="col-lg-6 col-12">
            <div className="d-flex align-items-center justify-content-between gap-2 p-3 rounded-3"
                style={{ background: '#fff', border: '1px solid #f0f0f0', boxShadow: '0 1px 6px rgba(0,0,0,0.04)' }}>
                <div className="d-flex align-items-center gap-3 overflow-hidden">
                    <div className="rounded-circle d-flex align-items-center justify-content-center fw-bold flex-shrink-0"
                        style={{ width: 36, height: 36, background: 'rgba(245,81,69,0.12)', color: '#F55145', fontSize: 12 }}>
                        {serial}
                    </div>
                    <div className="overflow-hidden">
                        <p style={labelStyle} className="mb-0">Word</p>
                        <p className="mb-0 fw-semibold text-truncate" style={{ color: '#1a1a2e', fontSize: 14 }}>{word}</p>
                    </div>
                </div>
                <div className="d-flex gap-2 flex-shrink-0">
                    {checkAccess(12, 3) && (
                        <button type="button" className="btn"
                            style={{ background: '#f5eeff', color: '#A44CD7', borderRadius: 8, padding: '4px 10px', fontSize: 12, border: '1px solid #e0c8ff' }}
                            onClick={() => openEdit(word)}>
                            <i className="bi bi-pencil me-1"></i>Edit
                        </button>
                    )}
                    {checkAccess(12, 4) && (
                        <button type="button" className="btn"
                            style={{ background: '#fff0f0', color: '#e03131', borderRadius: 8, padding: '4px 10px', fontSize: 12, border: '1px solid #ffd0d0' }}
                            onClick={() => handleDelete(word)}>
                            <i className="icon-trash me-1"></i>Delete
                        </button>
                    )}
                </div>
            </div>
        </div>
    )

    return (
        <>
            {loading && <Loader />}
            <ContainerDashboard title="Restricted Words" description="Manage words restricted for kids in the app">
                <section className="ps-dashboards p-4">

                    {/* Info Banner */}
                    <div className="rounded-4 p-4 mb-4 position-relative overflow-hidden"
                        style={{ background: 'linear-gradient(135deg, rgb(164, 76, 215) 0%, rgb(12, 172, 194) 100%)' }}>
                        <div style={{ position: 'absolute', top: -30, right: -30, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.07)' }} />
                        <div style={{ position: 'absolute', bottom: -40, right: 160, width: 110, height: 110, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
                        <div className="position-relative d-flex align-items-center gap-3">
                            <span style={{ fontSize: 40, lineHeight: 1, flexShrink: 0 }}><img height={40} src={restrictedImg} /></span>
                            <div className="flex-grow-1">
                                <h5 className="fw-bold mb-1" style={{ color: '#fff' }}>Restricted Words</h5>
                                <p className="mb-0" style={{ color: 'rgba(255,255,255,0.82)', fontSize: 14 }}>
                                    Words added here will be flagged or blocked when used by kids in the app.
                                </p>
                            </div>
                            {checkAccess(12, 2) && (
                                <button type="button" className="button btn-green" style={{ flexShrink: 0 }} onClick={openAdd}>
                                    <i className="icon-plus me-2"></i>Add Word
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Stats + Search row */}
                    <div className="d-flex align-items-center justify-content-between mb-4 gap-3 flex-wrap">
                        <div className="rounded-4 p-3 d-flex align-items-center gap-3"
                            style={{ background: '#fff', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', minWidth: 200 }}>
                            <div className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: 48, height: 48, background: 'rgba(245,81,69,0.12)' }}>
                                <i className="bi bi-slash-circle" style={{ fontSize: 22, color: '#F55145' }}></i>
                            </div>
                            <div>
                                <p className="mb-0" style={{ fontSize: 11, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Words</p>
                                <h4 className="mb-0 fw-bold" style={{ color: '#1a1a2e' }}>{words.length}</h4>
                            </div>
                        </div>

                        <div className="d-flex align-items-center">
                            <div className="ps-form--search-simple">
                                <input
                                    className="form-control bg-white"
                                    type="text"
                                    value={searchQuery}
                                    onChange={onSearch}
                                    placeholder="Search restricted words..."
                                />
                                <button type="button">
                                    <i className="icon icon-magnifier"></i>
                                </button>
                            </div>
                            <PerPageSelect itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} setPage={setPage} />
                        </div>
                    </div>

                    {/* Section heading */}
                    <h6 className="fw-bold mb-3" style={{ color: '#1a1a2e', fontSize: 16 }}>
                        Word List
                        <span className="ms-2 badge rounded-pill"
                            style={{ background: 'rgba(245,81,69,0.12)', color: '#F55145', fontSize: 12 }}>
                            {filteredWords.length}
                        </span>
                        {searchQuery && (
                            <span className="ms-2 text-muted fw-normal" style={{ fontSize: 13 }}>
                                — results for "{searchQuery}"
                            </span>
                        )}
                    </h6>

                    {/* Words Grid — responsive: 2 cols desktop, 1 col tablet */}
                    {filteredWords.length === 0 ? (
                        <div className="text-center py-5" style={{ color: '#aaa' }}>
                            <i className="bi bi-slash-circle" style={{ fontSize: 52, opacity: 0.3 }}></i>
                            <p className="mt-3 mb-0">
                                {searchQuery ? `No words match "${searchQuery}".` : 'No restricted words added yet.'}
                            </p>
                        </div>
                    ) : (
                        <div className="row g-3">
                            {pagedWords.map((word, i) => (
                                <WordCard key={word + i} word={word} serial={(page - 1) * itemsPerPage + i + 1} />
                            ))}
                        </div>
                    )}

                    <Pagination
                        page={page} totalPages={totalPages}
                        setPage={setPage} totalItems={filteredWords.length}
                        showingCount={pagedWords.length}
                    />

                </section>
            </ContainerDashboard>

            {/* Add / Edit Modal */}
            {showModal && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.48)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
                    onClick={e => { if (e.target === e.currentTarget) closeModal() }}>
                    <div style={{ width: '100%', maxWidth: 420, background: '#fff', borderRadius: 20, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.20)' }}>

                        <div className="d-flex align-items-center gap-3 px-4 py-3"
                            style={{ background: 'linear-gradient(135deg, #F55145, #A44CD7)' }}>
                            <span style={{ fontSize: 26 }}>{modalMode === 'edit' ? '✏️' : '🚫'}</span>
                            <div className="flex-grow-1">
                                <h6 className="mb-0 fw-bold text-white">
                                    {modalMode === 'edit' ? 'Edit Word' : 'Add Restricted Word'}
                                </h6>
                                <p className="mb-0" style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)' }}>
                                    {modalMode === 'edit'
                                        ? 'Update the restricted word.'
                                        : 'This word will be blocked when used by kids.'}
                                </p>
                            </div>
                            <button onClick={closeModal}
                                style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                ✕
                            </button>
                        </div>

                        <div className="p-4">
                            <div className="mb-4">
                                <label className="form-label fw-semibold" style={{ fontSize: 13 }}>
                                    Word <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className={`form-control ${wordError ? 'is-invalid' : ''}`}
                                    style={{ borderRadius: 10, fontSize: 14 }}
                                    placeholder="Enter a restricted word..."
                                    value={inputWord}
                                    autoFocus
                                    onChange={e => { setInputWord(e.target.value); setWordError('') }}
                                    onKeyDown={e => { if (e.key === 'Enter') handleSubmit() }}
                                />
                                {wordError && <div className="invalid-feedback d-block">{wordError}</div>}
                                <p className="mb-0 mt-1" style={{ fontSize: 11, color: '#aaa' }}>
                                    Saved in lowercase. Press Enter to submit quickly.
                                </p>
                            </div>

                            <div className="d-flex gap-2 justify-content-end">
                                <button className="btn px-4"
                                    style={{ borderRadius: 10, background: '#f5f5f5', color: '#555', fontSize: 13 }}
                                    onClick={closeModal}>
                                    Cancel
                                </button>
                                <button className="btn px-4 fw-semibold text-white" disabled={submitting}
                                    style={{ borderRadius: 10, background: 'linear-gradient(135deg, #F55145, #A44CD7)', fontSize: 13, border: 'none' }}
                                    onClick={handleSubmit}>
                                    <i className="bi bi-check2 me-1"></i>
                                    {submitting ? 'Saving…' : modalMode === 'edit' ? 'Save Changes' : 'Add Word'}
                                </button>
                            </div>
                        </div>

                    </div>
                </div>
            )}
        </>
    )
}

export default RestrictedWords
